import { CSSProperties, useState, useEffect } from 'react'
import useMaestroChat from 'Hooks/useMaestroChat'
import { MessageItem } from 'Hooks/useMaestroChat'
import Colors from 'modules/Colors'
import Icon from 'elements/Icon'
import ButtonSymphony from 'elements/ButtonSymphony'

interface MessageActionsProps {
    message: MessageItem
    showDevModeFeatures?: boolean // If true, shows additional dev-only features
    className?: string
    onAddMessage?: () => void // Callback to show add message UI
    isUserMessage?: boolean // Whether this is a user message (affects positioning)
}

const MessageActions = ({
    message,
    showDevModeFeatures = false,
    className,
    onAddMessage,
    isUserMessage = false
}: MessageActionsProps) => {
    const { 
        deleteMessage, 
        regenerateMessage, 
        devMode, 
        demoMode,
        updateMessage 
    } = useMaestroChat()
    
    const [isEditing, setIsEditing] = useState(false)
    const [editText, setEditText] = useState(message.text)
    const [showActions, setShowActions] = useState(false)
    const [hideMessageActions, setHideMessageActions] = useState(false)

    // Check if demo controls indicate message actions should be hidden
    useEffect(() => {
        if (demoMode) {
            const checkHideState = () => {
                const demoControlsElement = document.querySelector('[data-hide-message-actions]')
                if (demoControlsElement) {
                    const shouldHide = demoControlsElement.getAttribute('data-hide-message-actions') === 'true'
                    setHideMessageActions(shouldHide)
                }
            }
            
            // Check immediately
            checkHideState()
            
            // Set up an interval to check for changes
            const interval = setInterval(checkHideState, 100)
            
            return () => clearInterval(interval)
        }
    }, [demoMode])

    // Only show if dev mode or demo mode is active, and not explicitly hidden
    if (!devMode && !demoMode) {
        return null
    }
    
    // Hide if demo mode and user has toggled to hide message actions
    if (demoMode && hideMessageActions) {
        return null
    }

    const handleDelete = () => {
        if (window.confirm('Are you sure you want to delete this message?')) {
            deleteMessage(message.id)
        }
    }

    const handleResend = () => {
        regenerateMessage(message.id)
    }

    const handleEdit = () => {
        setIsEditing(true)
    }

    const handleSaveEdit = () => {
        if (updateMessage) {
            updateMessage(message.id, editText)
        } else {
            console.log('Edit functionality not yet implemented in useMaestroChat')
        }
        setIsEditing(false)
    }

    const handleCancelEdit = () => {
        setEditText(message.text)
        setIsEditing(false)
    }

    if (isEditing) {
        return (
            <div className={`messageEditMode ${className}`} style={sty.editMode}>
                <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    style={sty.editTextarea}
                    autoFocus
                    placeholder="Edit message..."
                />
                <div className="editButtons" style={sty.editButtons}>
                    <ButtonSymphony
                        className="cancelEditButton"
                        variant="text"
                        size="small"
                        onClick={handleCancelEdit}
                    >
                        Cancel
                    </ButtonSymphony>
                    <ButtonSymphony
                        className="saveEditButton"
                        variant="primary"
                        size="small"
                        onClick={handleSaveEdit}
                        disabled={editText.trim() === ''}
                    >
                        Save
                    </ButtonSymphony>
                </div>
            </div>
        )
    }

    return (
        <div 
            className={`messageActions ${className}`} 
            style={sty.container}
            onMouseEnter={() => setShowActions(true)}
            onMouseLeave={() => setShowActions(false)}
        >
            <div 
                className="actionButtons" 
                style={{
                    ...sty.actionButtons,
                    ...(isUserMessage ? sty.actionButtonsUser : sty.actionButtonsAssistant),
                    opacity: showActions || (devMode || demoMode) ? 1 : 0
                }}
            >
                {/* Add Message Button - Show first */}
                {onAddMessage && (
                    <button
                        className="actionButton addMessageButton"
                        style={sty.actionButton}
                        onClick={onAddMessage}
                        title="Add new message"
                        type="button"
                    >
                        <Icon className="addMessageIcon" icon="plus" size={14} color={Colors.purple} />
                    </button>
                )}

                {/* Edit Message */}
                <button
                    className="actionButton editButton"
                    style={sty.actionButton}
                    onClick={handleEdit}
                    title="Edit message"
                    type="button"
                >
                    <Icon className="editIcon" icon="edit" size={14} color={Colors.greyDark} />
                </button>

                {/* Resend/Regenerate Message */}
                <button
                    className="actionButton resendButton"
                    style={sty.actionButton}
                    onClick={handleResend}
                    title={message.isUser ? "Resend message" : "Regenerate response"}
                    type="button"
                >
                    <Icon className="resendIcon" icon="reload" size={14} color={Colors.greyDark} />
                </button>

                {/* Delete Message */}
                <button
                    className="actionButton deleteButton"
                    style={sty.actionButton}
                    onClick={handleDelete}
                    title="Delete message"
                    type="button"
                >
                    <Icon className="deleteIcon" icon="trash" size={14} color={Colors.red} />
                </button>

                {/* Dev Mode Only: Show message ID and timestamp */}
                {showDevModeFeatures && devMode && (
                    <button
                        className="actionButton devInfoButton"
                        style={sty.actionButton}
                        onClick={() => console.log('Message Debug Info:', message)}
                        title="Log message debug info"
                        type="button"
                    >
                        <Icon className="devInfoIcon" icon="info" size={14} color={Colors.purple} />
                    </button>
                )}
            </div>
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    container: {
        position: 'relative',
        display: 'inline-block',
        width: '100%',
    },
    actionButtons: {
        position: 'absolute',
        display: 'flex',
        gap: 4,
        backgroundColor: 'white',
        borderRadius: 8,
        padding: 4,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        border: `1px solid ${Colors.greyLight}`,
        transition: 'opacity 0.2s ease',
        zIndex: 100,
        top: -32, // Position above the message
    },
    actionButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        borderRadius: 4,
        border: 'none',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
    },
    editMode: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
        backgroundColor: Colors.greyLighter,
        borderRadius: 8,
        border: `1px solid ${Colors.greyLight}`,
        marginTop: 8,
        marginBottom: 8,
    },
    editTextarea: {
        width: '100%',
        minHeight: 80,
        padding: 8,
        borderRadius: 4,
        border: `1px solid ${Colors.grey}`,
        fontSize: 14,
        fontFamily: 'inherit',
        resize: 'vertical',
        outline: 'none',
        backgroundColor: 'white',
        color: Colors.greyDark, // Fix white text issue
    },
    editButtons: {
        display: 'flex',
        gap: 8,
        justifyContent: 'flex-end',
    },
    actionButtonsUser: {
        right: 0, // Anchor to top-right for user messages
    },
    actionButtonsAssistant: {
        left: 0, // Anchor to top-left for assistant messages
    },
}

export default MessageActions 